import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import {
  EntityType,
  ReportReason,
  ReportSeverityOptions,
  ReportSubmitPayload,
} from '../../../../shared/interfaces/report-content';
import { ReportsService } from '../../../../shared/services/reports/reports.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-flag-email',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './flag-email.component.html',
  styleUrl: './flag-email.component.scss',
})
export class FlagEmailComponent extends DestroyBase implements OnInit {
  private route = inject(ActivatedRoute);
  private reportsService = inject(ReportsService);
  private commonService = inject(CommonService);

  public token: string | null = null;
  public reportReasons: ReportReason[] = [];
  public selectedReason: ReportReason | null = null;
  public selectedSeverity = 1;
  public readonly severityOptions = ReportSeverityOptions;
  public details = '';

  public isLoadingReasons = false;
  public isSubmitting = false;
  public isSubmitted = false;
  public hasTokenError = false;
  public tokenErrorMessage = '';
  public submitErrorMessage: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        this.token = queryParams.get('token');

        if (!this.token) {
          this.hasTokenError = true;
          this.tokenErrorMessage =
            'No report token found in the link. Please ensure you are using the complete link from your email.';
          return;
        }

        this.fetchReportReasons();
      });
  }

  private fetchReportReasons(): void {
    if (!this.token) return;

    this.isLoadingReasons = true;
    this.hasTokenError = false;
    this.tokenErrorMessage = '';

    this.reportsService
      .getReportReasons(EntityType.EMAIL, this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (reasons) => {
          this.reportReasons = reasons || [];
          this.isLoadingReasons = false;
          if (this.reportReasons.length === 0) {
            this.hasTokenError = true;
            this.tokenErrorMessage =
              'No flagging reasons available at this time.';
          }
        },
        error: (err) => {
          this.isLoadingReasons = false;
          this.hasTokenError = true;
          if (err.status === 401 || err.status === 403) {
            this.tokenErrorMessage =
              'This email report link is invalid or has expired.';
          } else {
            this.tokenErrorMessage =
              err?.error?.detail ||
              'Failed to load report options. Please try again later.';
          }
        },
      });
  }

  public selectReason(reason: ReportReason): void {
    this.selectedReason = reason;
    this.submitErrorMessage = null;
  }

  public onSubmit(): void {
    if (!this.selectedReason || !this.token || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.submitErrorMessage = null;

    const payload: ReportSubmitPayload = {
      entity_id: '',
      entity_type: EntityType.EMAIL,
      reason_id: this.selectedReason.id,
      severity: this.selectedSeverity,
      details: this.details?.trim() || undefined,
    };

    this.reportsService
      .reportEmail(payload, this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.isSubmitted = true;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Email reported successfully',
          });
        },
        error: (err) => {
          this.isSubmitting = false;
          const errorMsg =
            err?.error?.detail || 'Failed to submit report. Please try again.';
          this.submitErrorMessage = errorMsg;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: errorMsg,
          });
        },
      });
  }
}
